import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Mail, Menu, Phone, X } from "lucide-react";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import logo from "../assets/WhatsApp Image 2024-12-03 at 3.41.47 PM.jpeg";
import { SITE, cn } from "../lib/site";
import useSession from "../lib/useSession";

// Site map. Groups become columns in the desktop mega-menu and accordions on mobile.
export const NAV = [
  {
    title: "About",
    groups: [
      {
        title: "Our school",
        links: [
          ["TIS history", "/Tishistory"],
          ["Head's message", "/HeadsMessage"],
          ["Our vision", "/OurVission"],
          ["Our values", "/OurValue"],
          ["Strategic plan", "/StrategicPlan"],
          ["Accreditation", "/Accreditation"],
        ],
      },
      {
        title: "People",
        links: [
          ["Board of Governors", "/BoardOfGoverners"],
          ["School leadership team", "/SchoolLeadershipTeam"],
          ["Parent council", "/ParentCouncil"],
          ["Organisational charts", "/OrganizationalCharts"],
        ],
      },
      {
        title: "See TIS",
        links: [
          ["Photo gallery", "/gallery"],
          ["Testimonials", "/testimonials"],
          ["Educational technology", "/educational-technology"],
          ["Job vacancies", "/JobVacancies"],
        ],
      },
    ],
  },
  {
    title: "Academics",
    groups: [
      {
        title: "IB Primary Years",
        links: [
          ["Primary Years Programme", "/PrimaryYearsProgram"],
          ["Pre-school", "/PreSchool"],
          ["Curriculum", "/Curriculum"],
          ["Assessment", "/Assessment"],
          ["Extra-curricular programme", "/ExtraCircullarProgram"],
        ],
      },
      {
        title: "IB Middle Years",
        links: [
          ["Middle Years Programme", "/MYP"],
          ["Global contexts", "/GlobalContexts"],
          ["Conceptual learning", "/ConceptualLearning"],
          ["Approaches to learning", "/ApproachesToLearn"],
          ["MYP projects", "/MYPProjects"],
          ["MYP assessments", "/MYPAssessments"],
          ["National requirements", "/NationalRequirements"],
        ],
      },
      {
        title: "IB Diploma",
        links: [
          ["IB Diploma Programme", "/IBDiploma"],
          ["Core programme", "/CoreProgram"],
          ["Language & literature", "/StudiesInLanguage"],
          ["Language acquisition", "/LanguageAcquisition"],
          ["Individuals & societies", "/IndividualsAndSocieties"],
          ["Sciences", "/Sciences"],
          ["Mathematics", "/Mathematics"],
          ["The arts", "/TheArts"],
          ["Award of the diploma", "/AwardOfDiploma"],
          ["Library", "/Library"],
        ],
      },
      {
        title: "More",
        links: [
          ["Matriculation division", "/matriculation", "new"],
          ["The IB continuum", "/INTERNATIONAL-BACCALAUREATE"],
          ["Culminating events", "/CulminatingEvents"],
          ["Action", "/Action"],
          ["International mindedness", "/InternationMindedness"],
        ],
      },
    ],
  },
  {
    title: "Admissions",
    groups: [
      {
        title: "Apply",
        links: [
          ["Admissions overview", "/admissions"],
          ["Apply online", "/admissions/signup"],
          ["Parent portal sign in", "/admissions/login"],
        ],
      },
      {
        title: "Information",
        links: [
          ["Admission process", "/Admission"],
          ["Admission requirements", "/AdmissionRequirements"],
          ["Fee structure", "/FeeStructure"],
          ["Scholarships", "/Scholarship"],
          ["Meet the admissions team", "/MeetTheAdmissionTeam"],
        ],
      },
    ],
  },
  {
    title: "Life at TIS",
    groups: [
      {
        title: "Student life",
        links: [
          ["Student personal development", "/StudentPersonalDevelopment"],
          ["CAS programme", "/CASProgram"],
          ["Societies & clubs", "/SocietiesAndClub"],
          ["TIS Parliament", "/TISParliament"],
          ["Africa, Europe & Asia", "/AfricaEuropeAsia"],
        ],
      },
      {
        title: "Support",
        links: [
          ["Counselling", "/Counselling"],
          ["Career & university counselling", "/CareerAndUniCounseling"],
          ["TIS language profile", "/TISLanguageProfile"],
          ["Code of conduct", "/CodeOfConduct"],
          ["Cafeteria", "/Cafeteria"],
        ],
      },
    ],
  },
  {
    title: "Alumni",
    groups: [
      {
        title: "TIS alumni",
        links: [
          ["Alumni network", "/alumni"],
          ["Alumni sign in", "/alumni/login"],
          ["Join the network", "/alumni/signup"],
          ["Getting involved", "/GettingInvolved"],
          ["Giving", "/Giving"],
        ],
      },
    ],
  },
  { title: "Contact", to: "/ContactDetails" },
];

function MegaPanel({ item, onNavigate }) {
  const wide = item.groups.length > 2;
  return (
    <div
      className={cn(
        "absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3",
        wide ? "w-[min(58rem,calc(100vw-2rem))]" : "w-[min(34rem,calc(100vw-2rem))]"
      )}
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lift">
        <div className={cn("grid gap-6", wide ? "grid-cols-4" : "grid-cols-2")} style={wide ? { gridTemplateColumns: `repeat(${item.groups.length}, minmax(0, 1fr))` } : undefined}>
          {item.groups.map((group) => (
            <div key={group.title}>
              <p className="mb-3 text-xs font-bold uppercase tracking-wider text-gold-600">{group.title}</p>
              <ul className="space-y-1">
                {group.links.map(([label, to, badge]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      onClick={onNavigate}
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 transition hover:bg-navy-50 hover:text-navy-800"
                    >
                      {label}
                      {badge && <span className="rounded-full bg-gold-400 px-1.5 py-0.5 text-[10px] font-bold uppercase text-navy-900">{badge}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const parent = useSession("parent");
  const [open, setOpen] = useState(null); // desktop menu title
  const [mobile, setMobile] = useState(false);
  const [expanded, setExpanded] = useState(null); // mobile accordion
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef(null);

  useEffect(() => {
    setOpen(null);
    setMobile(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobile]);

  const enter = (title) => {
    clearTimeout(closeTimer.current);
    setOpen(title);
  };
  const leave = () => {
    closeTimer.current = setTimeout(() => setOpen(null), 150);
  };

  const isActive = (item) =>
    item.to ? location.pathname === item.to : item.groups.some((g) => g.links.some(([, to]) => to.toLowerCase() === location.pathname.toLowerCase()));

  return (
    <header className="fixed inset-x-0 top-0 z-[100]">
      {/* utility bar */}
      <div className={cn("hidden bg-navy-900 text-navy-100 transition-all lg:block", scrolled ? "h-0 overflow-hidden" : "h-9")}>
        <div className="mx-auto flex h-9 max-w-7xl items-center justify-between px-6 text-xs">
          <div className="flex items-center gap-5">
            <a href={`tel:${SITE.uanTel}`} className="inline-flex items-center gap-1.5 hover:text-white">
              <Phone className="h-3.5 w-3.5" aria-hidden /> UAN {SITE.uan}
            </a>
            <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-1.5 hover:text-white">
              <Mail className="h-3.5 w-3.5" aria-hidden /> {SITE.email}
            </a>
          </div>
          <div className="flex items-center gap-5">
            <a href={SITE.manageBac} target="_blank" rel="noreferrer" className="font-semibold hover:text-white">
              ManageBac
            </a>
            <Link to="/alumni" className="font-semibold hover:text-white">
              Alumni
            </Link>
            <Link to={parent?.access ? "/portal" : "/admissions/login"} className="font-semibold hover:text-white">
              {parent?.access ? "My applications" : "Parent portal"}
            </Link>
            <span className="flex items-center gap-3 border-l border-white/15 pl-5">
              <a href={SITE.socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-white">
                <FaFacebookF />
              </a>
              <a href={SITE.socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-white">
                <FaInstagram />
              </a>
              <a href={SITE.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:text-white">
                <FaLinkedinIn />
              </a>
            </span>
          </div>
        </div>
      </div>

      {/* main bar */}
      <div className={cn("border-b bg-white/95 backdrop-blur transition-shadow", scrolled ? "border-slate-200 shadow-md" : "border-transparent")}>
        <div className="mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-3" aria-label="The International School home">
            <img src={logo} alt="" className="h-12 w-12 object-contain" />
            <span className="hidden font-display leading-tight sm:block">
              <span className="block text-[0.95rem] font-extrabold text-navy-800">The International School</span>
              <span className="block text-xs font-semibold text-gold-600">First IB World Continuum School in Pakistan</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            <NavLink to="/" end className={({ isActive: a }) => cn("rounded-lg px-3 py-2 text-sm font-semibold", a ? "text-navy-800" : "text-slate-700 hover:text-navy-800")}>
              Home
            </NavLink>
            {NAV.map((item) =>
              item.to ? (
                <NavLink
                  key={item.title}
                  to={item.to}
                  className={({ isActive: a }) => cn("rounded-lg px-3 py-2 text-sm font-semibold", a ? "text-navy-800" : "text-slate-700 hover:text-navy-800")}
                >
                  {item.title}
                </NavLink>
              ) : (
                <div key={item.title} className="relative" onMouseEnter={() => enter(item.title)} onMouseLeave={leave}>
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold transition",
                      open === item.title || isActive(item) ? "text-navy-800" : "text-slate-700 hover:text-navy-800"
                    )}
                    aria-expanded={open === item.title}
                    onClick={() => setOpen(open === item.title ? null : item.title)}
                  >
                    {item.title}
                    <ChevronDown className={cn("h-4 w-4 transition", open === item.title && "rotate-180")} aria-hidden />
                  </button>
                  {isActive(item) && <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded bg-gold-400" />}
                  {open === item.title && <MegaPanel item={item} onNavigate={() => setOpen(null)} />}
                </div>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/admissions"
              className="hidden rounded-xl bg-gold-400 px-5 py-2.5 text-sm font-bold text-navy-900 shadow-sm transition hover:bg-gold-300 sm:inline-flex"
            >
              Apply now
            </Link>
            <button
              type="button"
              className="rounded-lg p-2 text-navy-800 hover:bg-slate-100 lg:hidden"
              onClick={() => setMobile((m) => !m)}
              aria-label={mobile ? "Close menu" : "Open menu"}
              aria-expanded={mobile}
            >
              {mobile ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>
      </div>

      {/* mobile menu */}
      {mobile && (
        <div className="h-[calc(100dvh-4.5rem)] overflow-y-auto bg-white px-4 pb-10 pt-2 lg:hidden">
          <Link to="/admissions" className="mb-3 flex items-center justify-center rounded-xl bg-gold-400 py-3 font-bold text-navy-900">
            Apply now
          </Link>
          <Link to="/" className="block border-b border-slate-100 py-3.5 font-display font-bold text-navy-800">
            Home
          </Link>
          {NAV.map((item) =>
            item.to ? (
              <Link key={item.title} to={item.to} className="block border-b border-slate-100 py-3.5 font-display font-bold text-navy-800">
                {item.title}
              </Link>
            ) : (
              <div key={item.title} className="border-b border-slate-100">
                <button
                  type="button"
                  className="flex w-full items-center justify-between py-3.5 font-display font-bold text-navy-800"
                  onClick={() => setExpanded(expanded === item.title ? null : item.title)}
                  aria-expanded={expanded === item.title}
                >
                  {item.title}
                  <ChevronDown className={cn("h-5 w-5 transition", expanded === item.title && "rotate-180")} aria-hidden />
                </button>
                {expanded === item.title && (
                  <div className="space-y-4 pb-4">
                    {item.groups.map((group) => (
                      <div key={group.title}>
                        <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gold-600">{group.title}</p>
                        {group.links.map(([label, to]) => (
                          <Link key={to} to={to} className="block rounded-lg px-2 py-2 text-slate-700 hover:bg-navy-50">
                            {label}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          )}
          <div className="mt-6 grid grid-cols-2 gap-3 text-sm font-semibold">
            <a href={SITE.manageBac} target="_blank" rel="noreferrer" className="rounded-xl bg-slate-100 px-4 py-3 text-center text-navy-800">
              ManageBac
            </a>
            <Link to={parent?.access ? "/portal" : "/admissions/login"} className="rounded-xl bg-slate-100 px-4 py-3 text-center text-navy-800">
              Parent portal
            </Link>
            <a href={`tel:${SITE.uanTel}`} className="col-span-2 rounded-xl bg-navy-800 px-4 py-3 text-center text-white">
              Call UAN {SITE.uan}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
